import { EVENTS } from '../core/events.js';

/**
 * @class OptionsManager
 * Manages carousel options, responsive breakpoints, and merged settings.
 * - merges default options, user options, and a single active breakpoint (non-cumulative)
 * - reads options from a DOM element or programmatic updates
 * - writes merged options to the data store
 * - re-evaluates the current breakpoint on window resize/orientation change
 */
export default class OptionsManager {
	/**
	 * Creates a new OptionsManager instance
	 * @param {object} ctx - Shared module context (should contain .carousel, .viewport, .emitter, etc)
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;

		/** @type {object} - Default carousel options */
		_.defaultOptions = {
			/** @type {boolean|string} - Selector for carousel to sync navigation with */
			asNavFor: false,

			/** @type {object} - Physics-based animation settings */
			animation: {
				/** @type {number} - Spring attraction coefficient */
				attraction: 0.026,
				/** @type {number} - Friction coefficient for dampening */
				friction: 0.25,
				/** @type {number} - Base animation speed */
				speed: 5,
				/** @type {number} - Multiplier for initial velocity */
				velocityBoost: 1.4,
				/** @type {number} - Friction for free scroll momentum (0-1, higher = more slippery) */
				freeScrollFriction: 0.96,
			},

			/** @type {object} - Autoplay settings */
			autoplay: {
				/** @type {number} - Time between slides in ms (0 = disabled) */
				interval: 0,
				/** @type {boolean} - Whether to stop autoplay after user interaction */
				stopAfterInteraction: true,
				/** @type {string} - What happens to autoplay after user interaction */
				afterInteraction: 'pause', // stop or pause
			},

			/** @type {object} - Responsive breakpoint settings: { [minWidth:number]: optionsObject } */
			breakpoints: {},

			/** @type {string} - Uses either "viewport" or "window" for breakpoints */
			breakpointElement: 'window',

			/** @type {boolean} - Visually center the selected slide */
			centerSelectedSlide: false,

			/** @type {boolean} - Whether the carousel can be dragged */
			draggable: true,

			/** @type {number} - Minimum drag distance to trigger slide change */
			dragThreshold: 40,

			/** @type {string} - Class to filter which slides are included */
			filterClass: '',

			/** @type {boolean} - Whether clicking a slide selects it */
			focusOnSelect: false,

			/** @type {number} - Starting slide index */
			initialIndex: 0,

			/** @type {boolean} - Whether carousel should loop */
			loop: false,

			/** @type {string} - Snap behavior: 'page' (default), 'slide' (free scroll + snap), 'none' (free scroll) */
			snap: 'page',

			/** @type {string} - Display effect ('carousel', 'fade', etc) */
			effect: 'carousel',

			/** @type {number|string} - Gap between slides (px or CSS string) */
			gap: 0,

			/** @type {number|string} - Left padding (px or CSS string) */
			paddingLeft: 0,

			/** @type {number|string} - Right padding (px or CSS string)  */
			paddingRight: 0,

			/** @type {string} - Min width for slides */
			slideMinWidth: '50px',

			/** @type {number} - Slides visible at once */
			slidesPerView: 1,

			/** @type {number} - Slides to move on navigation */
			slidesPerMove: 'auto',

			/** @type {object} - Navigation controls settings */
			navigation: {
				/** @type {boolean} - Whether to show navigation buttons */
				showButtons: true,
				/** @type {boolean} - Whether to show previous button */
				showPreviousButton: true,
				/** @type {boolean} - Whether to show next button */
				showNextButton: true,
				/** @type {boolean|string} - Custom selector for previous button */
				previousButtonSelector: false,
				/** @type {boolean|string} - Custom selector for next button */
				nextButtonSelector: false,
				/** @type {boolean} - Whether buttons should hide when navigation limit reached */
				smartButtons: false,
				/** @type {boolean} - Whether to show pagination */
				showPagination: true,
				/** @type {boolean|string} - Custom selector for pagination container */
				paginationSelector: false,
			},

			/** @type {boolean} - Automatically go to selected slide */
			goToSelectedSlide: false,

			/** @type {boolean|string} - Selector for carousel to sync with */
			syncWith: false,

			/** @type {boolean} - Enable screen reader announcements for slide navigation */
			announcements: true,

			/** @type {boolean} - Write per-frame slide metric CSS vars (--tarot-visibility, --tarot-parallax, etc.) for CSS-driven styling */
			renderSlideMetrics: false,

			/**
			 * Element used to compute slide visibility for inert/aria-hidden.
			 * Does NOT affect layout, snap, loop, or effect math — only the inert/aria-hidden bounds.
			 * @type {'viewport'|'carousel'|string|HTMLElement}
			 *   'viewport' (default) - use tarot-viewport (current behavior)
			 *   'carousel'           - use the outer tarot-carousel element
			 *   string               - CSS selector resolved via carousel.closest(selector)
			 *   HTMLElement          - direct element reference
			 * The element MUST be in a stable layout relationship with the viewport (i.e. an ancestor).
			 */
			visibilityBoundsElement: 'viewport',

			/** @type {object} - Effect rules (not user-settable, overridden by effect class) */
			rules: {
				minSlidesPerView: 1,
				maxSlidesPerView: Infinity,
				loopBuffer: { left: 0, right: 1 },
				minPaddingLeft: 0,
				minPaddingRight: 0,
			},
		};

		/** @type {object} - User-provided options (pre-merge) */
		_.userOptions = {};

		/** @type {HTMLElement|null} - Element containing data-tarot-options JSON */
		_.userOptionsElement = _.ctx.carousel.querySelector(':scope > [data-tarot-options]');

		/** @type {{minWidth:number,options:object}} - Current active breakpoint */
		_.currentBreakpoint = { minWidth: 0, options: {} };

		/**
		 * debounced version of checkBreakpoints used only for noisy window events
		 * - we use trailing execution so multiple rapid events collapse into one
		 * - programmatic calls (reInit/setUserOptions) still run immediately
		 * @type {Function}
		 */
		_.checkBreakpointsDebounced = _.ctx.utils.debounce(() => _.checkBreakpoints(), 4);

		// event handlers bundle (debounced)
		_.handlers = {
			/** @type {Function} - Called on window resize; debounced to avoid duplicate work */
			onWindowResize: _.checkBreakpointsDebounced,
			/** @type {Function} - Called on orientation change; debounced to coalesce with resize */
			onOrientationChange: _.checkBreakpointsDebounced,
		};

		_.init();
	}

	/**
	 * Initializes the options manager
	 * - loads user options from DOM
	 * - computes initial breakpoint
	 * - writes merged options to the data store
	 */
	init() {
		const _ = this;
		_.bindEvents();
		_.loadUserOptions();
		_.currentBreakpoint = _.getCurrentBreakpoint();
		_.applyMergedOptions();
	}

	/**
	 * Triggers a re-evaluation of breakpoints and updates merged options if needed
	 * - this is intentionally immediate (not debounced) for programmatic calls
	 */
	reInit() {
		this.checkBreakpoints();
	}

	/**
	 * Binds to relevant window events for breakpoints
	 */
	bindEvents() {
		const _ = this;
		_.ctx.emitter.on(_.ctx.events.window.resize, _.handlers.onWindowResize);
		_.ctx.emitter.on(_.ctx.events.window.orientationChange, _.handlers.onOrientationChange);
	}

	/**
	 * Cleans up event listeners and cancels any pending debounced breakpoint checks
	 */
	destroy() {
		const _ = this;
		_.ctx.emitter.off(_.ctx.events.window.resize, _.handlers.onWindowResize);
		_.ctx.emitter.off(_.ctx.events.window.orientationChange, _.handlers.onOrientationChange);
		if (_.checkBreakpointsDebounced && typeof _.checkBreakpointsDebounced.cancel === 'function') {
			_.checkBreakpointsDebounced.cancel();
		}

		// Clear references for garbage collection
		_.ctx = null;
		_.handlers = null;
		_.checkBreakpointsDebounced = null;
		_.userOptionsElement = null;
		_.defaultOptions = null;
		_.userOptions = null;
		_.currentBreakpoint = null;
	}

	/**
	 * Updates user options (programmatic API), merges, and checks breakpoints
	 * @param {object} newOptions - New user-supplied options
	 * @returns {OptionsManager}
	 */
	setUserOptions(newOptions = {}) {
		const _ = this;
		_.userOptions = _.ctx.utils.deepMerge(_.userOptions, newOptions);
		_.currentBreakpoint = _.getCurrentBreakpoint();
		_.applyMergedOptions();
		return _;
	}

	/**
	 * Loads user-supplied options from a DOM element (if present)
	 */
	loadUserOptions() {
		const _ = this;
		if (!_.userOptionsElement) return;

		let txt = _.userOptionsElement.textContent || '';
		txt = txt.replace(/\n/g, '').trim();
		// Accept unquoted option keys without rewriting key-like text inside quoted values.
		const unquotedKeyPattern =
			/([{,]\s*)([A-Za-z_$][\w$]*)\s*:(?=(?:[^"\\]|\\.|"(?:[^"\\]|\\.)*")*$)/g;
		txt = txt.replace(unquotedKeyPattern, '$1"$2":');

		// Accept trailing commas before object/array endings.
		const trailingCommaPattern = /,\s*([}\]])(?=(?:[^"\\]|\\.|"(?:[^"\\]|\\.)*")*$)/g;
		txt = txt.replace(trailingCommaPattern, '$1');

		try {
			_.userOptions = JSON.parse(txt);
		} catch (err) {
			console.error('tarot options: failed to parse data-tarot-options json', err);
		}
	}

	/**
	 * Computes which breakpoint applies based on the current measured width
	 * @returns {{ minWidth:number, options:object }}
	 */
	getCurrentBreakpoint() {
		const _ = this;

		// isolate breakpoints and build a base (defaults + pluginDefaults + user) to know what to measure
		const { breakpoints = {}, ...userBase } = _.userOptions;
		const pluginDefaults = _.ctx.getPluginDefaults?.() || {};
		let baseOptions = _.ctx.utils.deepMerge(_.defaultOptions, pluginDefaults);
		baseOptions = _.ctx.utils.deepMerge(baseOptions, userBase);

		// get width of either the viewport element or the window
		let currentWidth;
		if (baseOptions.breakpointElement === 'viewport' && _.ctx.viewport) {
			currentWidth = _.ctx.viewport.offsetWidth;
		} else {
			currentWidth = window.innerWidth;
		}

		// no breakpoints defined → return empty descriptor
		if (!breakpoints || typeof breakpoints !== 'object') {
			return { minWidth: 0, options: {} };
		}

		// convert keys to numbers and sort ascending
		const breakpointWidths = Object.keys(breakpoints)
			.map(Number)
			.filter((n) => !Number.isNaN(n))
			.sort((a, b) => a - b);

		// pick the largest breakpoint ≤ currentWidth
		let matchingBreakpointWidth = 0;
		for (let i = 0; i < breakpointWidths.length; i++) {
			const breakpointWidth = breakpointWidths[i];

			if (currentWidth >= breakpointWidth) {
				matchingBreakpointWidth = breakpointWidth;
			} else {
				break;
			}
		}

		return {
			minWidth: matchingBreakpointWidth,
			options: breakpoints[matchingBreakpointWidth] || {},
		};
	}

	/**
	 * Checks if breakpoint has changed, applies merged options if so
	 */
	checkBreakpoints() {
		const _ = this;
		const active = _.getCurrentBreakpoint();
		if (active.minWidth === _.currentBreakpoint.minWidth) return;

		_.currentBreakpoint = active;
		_.applyMergedOptions();
	}

	/**
	 * Merges default, user, and active breakpoint options and writes to data store
	 */
	applyMergedOptions() {
		const _ = this;
		const { breakpoints: _bp, rules: _ur, ...userBase } = _.userOptions;
		const bpOptions = _.currentBreakpoint.options || {};
		const { rules: _br, ...bpBase } = bpOptions;

		const pluginDefaults = _.ctx.getPluginDefaults?.() || {};
		let merged = _.ctx.utils.deepMerge(_.defaultOptions, pluginDefaults);
		merged = _.ctx.utils.deepMerge(merged, userBase);
		merged = _.ctx.utils.deepMerge(merged, bpBase);
		delete merged.breakpoints;

		// Apply effect rules (not user-settable, effect class always wins)
		const effectClass = _.ctx.getEffectClass?.(merged.effect);
		if (effectClass?.rules) {
			merged.rules = { ...merged.rules, ...effectClass.rules };
		}

		if (merged.slidesPerView < 1) merged.slidesPerView = 1;

		// Clamp slidesPerView to effect rules
		const { minSlidesPerView, maxSlidesPerView } = merged.rules;
		merged.slidesPerView = Math.max(
			minSlidesPerView,
			Math.min(maxSlidesPerView, merged.slidesPerView)
		);

		// Resolve 'auto' slidesPerMove after slidesPerView is finalized
		if (merged.slidesPerMove === 'auto') {
			merged.slidesPerMove = Math.floor(merged.slidesPerView);
		}

		_.ctx.store.setOptions(merged);
	}
}
